import {
  LOCATION_PRESETS,
  type LocationType,
  locationNameKey,
  locationTypeFor,
  MAX_LOCATION_NAME,
  NAME_PROBLEM_TEXT,
  validateLocationName,
} from "@larder/shared";
import { useState } from "react";

/**
 * First run: name the household.
 *
 * A location is a household, not a shelf — see the note on `LocationType`. The
 * copy avoids the word "location" entirely because the app also uses it for
 * fridge-vs-pantry, and a first-time user has no way to tell those apart.
 *
 * Two taps to done: "Home" is a preset chip that fills the name and the type,
 * so the common case never touches the keyboard. Everything else about the
 * household (timezone) is inferred. See docs/product/onboarding.md.
 */
export function CreateLocation({
  onCreate,
  onCancel,
  busy,
  error,
  existingCount,
  existingNames,
}: {
  onCreate: (name: string, type: LocationType) => void;
  onCancel: () => void;
  busy: boolean;
  error: string | null;
  existingCount: number;
  /** Names already in use, so a second "Home" is refused before it exists. */
  existingNames: readonly string[];
}) {
  const [name, setName] = useState("");
  const [touched, setTouched] = useState(false);

  const problem = validateLocationName(name, existingNames);
  const first = existingCount === 0;
  const takenKeys = new Set(existingNames.map(locationNameKey));

  // Type is derived from the name, never held separately — see locationTypeFor.
  const type: LocationType = locationTypeFor(name);

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setTouched(true);
    if (problem) return;
    onCreate(name, type);
  }

  return (
    <section aria-label="create a place">
      <header className="detail-head">
        <button
          type="button"
          className="back"
          data-testid="back-btn"
          onClick={onCancel}
          aria-label="Back"
        >
          <span aria-hidden="true">←</span>
        </button>
        <div className="detail-ident">
          <h2>{first ? "Where are we tracking?" : "Add another place"}</h2>
        </div>
      </header>
      <p className="tagline">
        {first
          ? "Your kitchen, your desk drawer — anywhere things go off. You can add more later."
          : "A second place keeps its own list and its own reminders."}
      </p>

      <form onSubmit={submit}>
        <div className="chips">
          {/* A preset you already have is shown but not offered. Hiding it
              would make "where did Home go?" a question; greying it answers
              the question before it is asked. */}
          {LOCATION_PRESETS.map((p) => {
            const taken = takenKeys.has(locationNameKey(p.name));
            return (
              <button
                key={p.name}
                type="button"
                className={`chip ${type === p.type ? "on" : ""}`}
                data-testid={`preset-${p.type}`}
                disabled={taken}
                title={taken ? "You already have this one" : undefined}
                onClick={() => setName(p.name)}
              >
                {p.name}
              </button>
            );
          })}
        </div>

        <label className="field">
          <span className="label">Name</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => setTouched(true)}
            placeholder="Home"
            maxLength={MAX_LOCATION_NAME + 10}
            autoComplete="off"
            data-testid="location-name"
            // Android: the keyboard's action key should say "Done", not "Go".
            enterKeyHint="done"
          />
        </label>

        {touched && problem && (
          <p className="hint" data-testid="name-error">
            {NAME_PROBLEM_TEXT[problem]}
          </p>
        )}
        {error && (
          <p className="hint" data-testid="create-error">
            {error}
          </p>
        )}

        <button
          type="submit"
          className="primary"
          disabled={busy}
          data-testid="create-location"
        >
          {busy ? "Creating…" : first ? "Start tracking" : "Add place"}
        </button>
      </form>
    </section>
  );
}
